
import axios, { AxiosResponse } from 'axios'
import { base, clientId } from '@/axios_http/index'
import { ArtistTrackModel, PlaylistModel } from '@/models'
import { Component, Watch, Vue } from 'vue-property-decorator'

interface FeaturedGenre {
    name: string;
    link: string;
}

@Component
export default class Home extends Vue {
    loader = true
    error: string | null = null
    weeklyTracks: ArtistTrackModel[] = []
    discoveryTracks: ArtistTrackModel[] = []
    localTopTracks: ArtistTrackModel[] = []
    hasLoadedOnce = false
    featuredGenres: FeaturedGenre[] = [
        { name: 'Rock', link: 'rock' },
        { name: 'Jazz', link: 'jazz' },
        { name: 'Electronic', link: 'electronic' },
        { name: 'Classical', link: 'classical' },
        { name: 'Hip-Hop', link: 'hiphop' },
        { name: 'Pop', link: 'pop' },
        { name: 'Acoustic', link: 'acoustic' },
        { name: 'Ambient', link: 'ambient' },
    ]

    get radioSource() {
        return this.$store.getters['getRadioSource']
    }
    get displayTopTracks() {
        return this.radioSource === 'audius' || this.radioSource === 'suno' ? this.localTopTracks : (this.$store.getters['trackModule/getTopTracks'] || [])
    }
    get playlists(): PlaylistModel[] {
        return this.$store.getters['playlistModule/getTopPlaylists'] ? this.$store.getters['playlistModule/getTopPlaylists'] : []
    }
    get featuredPlaylists(): PlaylistModel[] {
        return this.playlists.slice(0, 8)
    }
    get audio () {
        return this.$store.getters['getAudio']
    }
    coverStyle(image?: string, name?: string) {
        if (image && image.trim() && image.indexOf('url("")') === -1) {
            return { backgroundImage: `var(--hero-overlay), url(${image})` }
        }
        const str = name || 'fallback';
        let hash = 0;
        for (let i = 0; i < str.length; i++) hash = str.charCodeAt(i) + ((hash << 5) - hash);
        const hue = Math.abs(hash) % 360;
        return {
            backgroundImage: `linear-gradient(135deg, hsl(${hue}, 70%, 20%), hsl(${(hue + 40) % 360}, 80%, 15%))`
        }
    }

    playAlbum(id: string) {
        this.$store.dispatch('trackModule/fetchAlbumTracks', id).then((response) => {
            if (!response || !response[0] || !response[0].tracks || !response[0].tracks.length) {
                return
            }
            response[0].tracks.map((track: ArtistTrackModel) => {
                track['artist_name'] = response[0].artist_name
                track.image = response[0].image
            })
            this.$store.commit('setTracksQueue', response[0].tracks)
            this.$store.commit('playTrack', response[0].tracks[0])
            this.audio.src = response[0].tracks[0].audio
            this.audio.play()
        })
    }

    playSong (track: ArtistTrackModel, queue: ArtistTrackModel[]) {
        this.audio.playbackRate = 1
        this.audio.src = track.audio
        this.$store.commit('setTracksQueue', queue)
        this.$store.commit('playTrack', track)
        this.audio.play();
    }

    getDateSeed() {
        const now = new Date()
        const key = `${now.getFullYear()}-${now.getMonth() + 1}-${now.getDate()}`
        let hash = 0
        for (let i = 0; i < key.length; i++) {
            hash = key.charCodeAt(i) + ((hash << 5) - hash)
        }
        return Math.abs(hash)
    }

    getRotatingOffset(limit: number, pageCount: number, salt = 0) {
        const seed = this.getDateSeed() + salt
        return (seed % pageCount) * limit
    }

    fetchTracks(limit: number, boost: string, offset = 0) {
        return axios.get(
            `${base}tracks/?client_id=${clientId}&limit=${limit}&format=jsonpretty&imagesize=600&boost=${boost}&offset=${offset}`
        ).then((response: AxiosResponse) => response.data.results || [])
    }

    @Watch('radioSource')
    onRadioSourceChanged() {
        this.loadContent()
    }

    async loadContent() {
        if (!this.hasLoadedOnce) {
            this.loader = true
        }
        window.scrollTo(0,0);
        try {
            if (this.radioSource === 'suno') {
                const [weeklyTracks, discoveryTracks, topTracks] = await Promise.all([
                    this.$store.dispatch('sunoModule/fetchRadioTracks', { limit: 12, genre: 'electronic' }),
                    this.$store.dispatch('sunoModule/fetchRadioTracks', { limit: 12, genre: 'pop' }),
                    this.$store.dispatch('sunoModule/fetchRadioTracks', { limit: 24 })
                ]);
                this.weeklyTracks = weeklyTracks
                this.discoveryTracks = discoveryTracks
                this.localTopTracks = topTracks
            } else if (this.radioSource === 'audius') {
                const [weeklyTracks, discoveryTracks, topTracks] = await Promise.all([
                    this.$store.dispatch('audiusModule/fetchRadioTracks', { limit: 12, genre: 'electronic' }),
                    this.$store.dispatch('audiusModule/fetchRadioTracks', { limit: 12, genre: 'hiphop' }),
                    this.$store.dispatch('audiusModule/fetchRadioTracks', { limit: 24 })
                ]);
                this.weeklyTracks = weeklyTracks
                this.discoveryTracks = discoveryTracks
                this.localTopTracks = topTracks
            } else {
                const weeklyOffset = this.getRotatingOffset(12, 18, 17)
                const discoveryOffset = this.getRotatingOffset(12, 30, 29)

                const [weeklyTracks, discoveryTracks] = await Promise.all([
                    this.fetchTracks(12, 'popularity_week', weeklyOffset),
                    this.fetchTracks(12, 'popularity_month', discoveryOffset),
                    this.$store.dispatch('trackModule/fetchTopTracks', 24),
                ])
                this.weeklyTracks = weeklyTracks
                this.discoveryTracks = discoveryTracks
            }
            this.$store.dispatch('playlistModule/loadPlaylists')
            this.hasLoadedOnce = true
            this.loader = false
        } catch (error) {
            this.loader = false
            this.error = error instanceof Error ? error.message : 'Failed to load content.'
        }
    }

    async created () {
        this.loadContent()
    }
}
